import type { ComboboxItem } from '@/components'
import {
  Button,
  Combobox,
  FAIcon,
  Field,
  FieldLabel,
  ModuleWrapper,
} from '@/components'
import {
  VehicleExpirationTypeCombobox,
  WorkshopCombobox,
} from '@/components/combobox'
import type {
  VehicleServicePlanExportFilters,
  VehicleServicePlansQuery,
  VehicleServicePlansQueryVariables,
  VehicleServicePlanSource,
  VehicleServicePlanStatus,
} from '@/modules/vehicles'
import {
  VEHICLE_SERVICE_PLANS_QUERY,
  vehicleServicePlanStatuses,
} from '@/modules/vehicles'
import type { VehicleListFilterForm } from '@/modules/vehicles/list-filters'
import {
  emptyVehicleListFilter,
  getDateRangeBounds,
  getVehicleListFilterVariables,
  VehicleListFilters,
} from '@/modules/vehicles/list-filters'
import { VehicleListExportButton } from '@/modules/vehicles/list-export'
import { PERMISSIONS } from '@/utils'
import {
  useDebounce,
  useNodes,
  useOnErrorAuto,
  usePermissions,
} from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import {
  useServicePlanSourceLabels,
  useServicePlanStatusLabels,
} from './service-plan-status'
import { useVehicleServicePlanHandlers } from './use-vehicle-service-plan-handlers'
import { VehicleServicePlansTable } from './vehicle-service-plans-table'

const servicePlanSources = [
  'MANUAL',
  'SCANIA',
] as const satisfies readonly VehicleServicePlanSource[]

/**
 * The shared filters plus the ones only a service has: the type, the workshop,
 * the booking status and where the row came from. The shared `expirationStatus` is unused
 * here — a service is planned or booked, not expiring — so the card hides it.
 */
interface ServicePlanFilterForm extends VehicleListFilterForm {
  serviceTypes: string[]
  workshops: string[]
  statuses: VehicleServicePlanStatus[]
  sources: VehicleServicePlanSource[]
}

/**
 * The list opens on the visits still to happen. Performed services accumulate
 * forever and pushed the planned ones -- what the page is actually used to
 * follow up -- off the first screens.
 */
const defaultStatuses = [
  'PLANNED',
  'BOOKED',
] as const satisfies readonly VehicleServicePlanStatus[]

const emptyFilter: ServicePlanFilterForm = {
  ...emptyVehicleListFilter,
  serviceTypes: [],
  workshops: [],
  statuses: [...defaultStatuses],
  sources: [],
}

export const VehicleServicePlansView: React.FC = () => {
  const translations = useTranslate({
    title: 'vehicles.vehicle-services',
    searchPlaceholder: 'vehicles.services.search-placeholder',
    startDate: 'common.start-date',
    type: 'common.type',
    workshop: 'vehicles.service.workshop',
    status: 'common.status',
    source: 'common.source',
    select: 'common.select-ellipsis',
    add: 'vehicles.new-vehicle-service',
  })

  const [filter, setFilter] = useState<ServicePlanFilterForm>(emptyFilter)

  // Only the free-text search is debounced; the other filters change one click
  // at a time and should apply immediately.
  const debouncedQ = useDebounce(filter.q)

  // Ordering is applied at the query, not here: the export takes the same
  // filters and its input has no `orderBy`.
  const filterVariables = useMemo<VehicleServicePlanExportFilters>(() => {
    const { from, to } = getDateRangeBounds(filter.dateRange)

    return {
      ...getVehicleListFilterVariables({ ...filter, q: debouncedQ }),
      serviceTypes:
        filter.serviceTypes.length > 0 ? filter.serviceTypes : undefined,
      workshops: filter.workshops.length > 0 ? filter.workshops : undefined,
      statuses: filter.statuses.length > 0 ? filter.statuses : undefined,
      sources: filter.sources.length > 0 ? filter.sources : undefined,
      scheduledStartGte: from,
      scheduledStartLte: to,
    }
  }, [filter, debouncedQ])

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<
    VehicleServicePlansQuery,
    VehicleServicePlansQueryVariables
  >(VEHICLE_SERVICE_PLANS_QUERY, {
    variables: { ...filterVariables, orderBy: 'scheduledStart' },
    onError: onErrorAuto(),
  })

  const servicePlans = useNodes(data?.allVehicleServicePlans)

  const statusLabels = useServicePlanStatusLabels()
  const statusItems = useMemo<ComboboxItem<VehicleServicePlanStatus>[]>(
    () =>
      vehicleServicePlanStatuses.map(status => ({
        value: status,
        label: statusLabels[status],
      })),
    [statusLabels]
  )

  const sourceLabels = useServicePlanSourceLabels()
  const sourceItems = useMemo<ComboboxItem<VehicleServicePlanSource>[]>(
    () =>
      servicePlanSources.map(source => ({
        value: source,
        label: sourceLabels[source],
      })),
    [sourceLabels]
  )

  const {
    addServicePlan,
    editServicePlan,
    removeServicePlan,
    moveToExpirations,
  } = useVehicleServicePlanHandlers()

  const { hasPermissions } = usePermissions()
  // Services reuse the expiration permissions; see the table for why.
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  // The status filter starts on the default, so it only counts as narrowing
  // the list once the user changes it -- otherwise "clear filters" would offer
  // to undo a filter the user never set, and clearing would not remove it.
  const statusFilterChanged =
    filter.statuses.length !== defaultStatuses.length ||
    defaultStatuses.some(status => !filter.statuses.includes(status))

  const extraFilterActive =
    filter.serviceTypes.length > 0 ||
    filter.workshops.length > 0 ||
    statusFilterChanged ||
    filter.sources.length > 0

  return (
    <ModuleWrapper
      title={translations.title}
      headerRight={
        <div className="flex items-center gap-2">
          <VehicleListExportButton kind="services" filters={filterVariables} />
          {canAdd && (
            <Button onClick={() => void addServicePlan()}>
              <FAIcon icon="plus" />
              {translations.add}
            </Button>
          )}
        </div>
      }
    >
      <VehicleListFilters
        filter={filter}
        searchPlaceholder={translations.searchPlaceholder}
        showExpirationStatus={false}
        dateRangeLabel={translations.startDate}
        extraFilterActive={extraFilterActive}
        onChange={patch => setFilter(current => ({ ...current, ...patch }))}
        onClear={() => setFilter(emptyFilter)}
      >
        <Field>
          <FieldLabel>{translations.type}</FieldLabel>
          <VehicleExpirationTypeCombobox
            mode="multiple"
            value={filter.serviceTypes}
            className="max-w-[350px]"
            onChange={serviceTypes =>
              setFilter(current => ({ ...current, serviceTypes }))
            }
          />
        </Field>

        <Field>
          <FieldLabel>{translations.workshop}</FieldLabel>
          <WorkshopCombobox
            mode="multiple"
            value={filter.workshops}
            className="max-w-[350px]"
            onChange={workshops =>
              setFilter(current => ({ ...current, workshops }))
            }
          />
        </Field>

        <Field>
          <FieldLabel>{translations.status}</FieldLabel>
          <Combobox<VehicleServicePlanStatus>
            mode="multiple"
            value={filter.statuses}
            items={statusItems}
            noSearch
            className="max-w-[350px]"
            placeholder={translations.select}
            onChange={statuses =>
              setFilter(current => ({ ...current, statuses }))
            }
          />
        </Field>

        <Field>
          <FieldLabel>{translations.source}</FieldLabel>
          <Combobox<VehicleServicePlanSource>
            mode="multiple"
            value={filter.sources}
            items={sourceItems}
            noSearch
            className="max-w-[350px]"
            placeholder={translations.select}
            onChange={sources =>
              setFilter(current => ({ ...current, sources }))
            }
          />
        </Field>
      </VehicleListFilters>

      <VehicleServicePlansTable
        servicePlans={servicePlans}
        loading={loading}
        onEdit={editServicePlan}
        onDelete={removeServicePlan}
        onMoveToExpirations={moveToExpirations}
      />
    </ModuleWrapper>
  )
}
